import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  Lightbulb,
  MessageSquare,
  ThumbsUp,
  TrendingUp,
  Activity,
  Shield,
  ArrowLeft,
  RefreshCw,
  Search,
  Filter,
  Calendar,
  CheckCircle,
  XCircle,
  FileText,
  Eye,
  Download,
} from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { useAdminActivities, useActivityStats } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { ActivityFilters } from "@/lib/activityApi";

const ActivityItem = ({ activity }) => {
  const getActivityIcon = () => {
    switch (activity.type) {
      case "USER_REGISTERED":
        return <Users size={14} className="text-blue-500 sm:w-4 sm:h-4" />;
      case "CAMPAIGN_CREATED":
        return <FileText size={14} className="text-purple-500 sm:w-4 sm:h-4" />;
      case "CAMPAIGN_UPDATED":
        return (
          <CheckCircle size={14} className="text-green-600 sm:w-4 sm:h-4" />
        );
      case "COMMENT_POSTED":
        return (
          <MessageSquare size={14} className="text-amber-500 sm:w-4 sm:h-4" />
        );
      case "RESPONSE_SUBMITTED":
        return (
          <MessageSquare size={14} className="text-blue-500 sm:w-4 sm:h-4" />
        );
      case "BLOOM_SCORE_GIVEN":
        return <ThumbsUp size={14} className="text-green-500 sm:w-4 sm:h-4" />;
      case "MILESTONE_REACHED":
        return (
          <TrendingUp size={14} className="text-purple-500 sm:w-4 sm:h-4" />
        );
      case "SIGNIFICANT_ENGAGEMENT":
        return <Activity size={14} className="text-orange-500 sm:w-4 sm:h-4" />;
      default:
        return <Activity size={14} className="text-gray-500 sm:w-4 sm:h-4" />;
    }
  };

  const getActivityTypeLabel = (type: string) => {
    switch (type) {
      case "USER_REGISTERED":
        return "User Registration";
      case "CAMPAIGN_CREATED":
        return "Campaign Created";
      case "CAMPAIGN_UPDATED":
        return "Campaign Updated";
      case "COMMENT_POSTED":
        return "Comment Posted";
      case "RESPONSE_SUBMITTED":
        return "Response Submitted";
      case "BLOOM_SCORE_GIVEN":
        return "Bloom Score Given";
      case "MILESTONE_REACHED":
        return "Milestone Reached";
      case "SIGNIFICANT_ENGAGEMENT":
        return "Significant Engagement";
      default:
        return type;
    }
  };

  const getActivityTypeColor = (type: string) => {
    switch (type) {
      case "USER_REGISTERED":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "CAMPAIGN_CREATED":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "CAMPAIGN_UPDATED":
        return "bg-green-100 text-green-800 border-green-200";
      case "COMMENT_POSTED":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "RESPONSE_SUBMITTED":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "BLOOM_SCORE_GIVEN":
        return "bg-green-100 text-green-800 border-green-200";
      case "MILESTONE_REACHED":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "SIGNIFICANT_ENGAGEMENT":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="flex items-start space-x-2 sm:space-x-3 py-2 sm:py-3 first:pt-0">
      <div className="mt-0.5">
        <div className="h-6 w-6 sm:h-7 sm:w-7 rounded-full bg-slate-100 flex items-center justify-center">
          {getActivityIcon()}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
          <Badge
            variant="outline"
            className={`text-xs ${getActivityTypeColor(activity.type)}`}
          >
            {getActivityTypeLabel(activity.type)}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {new Date(activity.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
        <p className="text-xs sm:text-sm text-foreground">
          {activity.description}
          {activity.user && (
            <span className="font-medium text-primary">
              {" "}
              by {activity.user.name}
            </span>
          )}
          {activity.campaign && (
            <span className="font-medium text-primary">
              {" "}
              on {activity.campaign.title}
            </span>
          )}
        </p>
      </div>
    </div>
  );
};

const AdminActivity: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ActivityFilters>({
    limit: 50,
    offset: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<string>("all");

  // Check if user is admin
  const isAdmin = isAuthenticated && user?.role === "ADMIN";

  // Fetch activities with filters - moved before early returns
  const {
    data: activities,
    isLoading: activitiesLoading,
    error: activitiesError,
  } = useAdminActivities(filters);

  const { data: activityStats, isLoading: statsLoading } = useActivityStats();

  // Filter activities based on search term and selected type
  const filteredActivities = useMemo(() => {
    if (!activities) return [];

    return activities.filter((activity) => {
      // Filter by type
      if (selectedType !== "all" && activity.type !== selectedType) {
        return false;
      }

      // Filter by search term
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesDescription = activity.description
          .toLowerCase()
          .includes(searchLower);
        const matchesUser = activity.user?.name
          .toLowerCase()
          .includes(searchLower);
        const matchesCampaign = activity.campaign?.title
          .toLowerCase()
          .includes(searchLower);

        return matchesDescription || matchesUser || matchesCampaign;
      }

      return true;
    });
  }, [activities, selectedType, searchTerm]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
                Access Denied
              </h1>
              <p className="text-sm sm:text-base text-slate-600">
                Please log in to access the admin activity log.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
                Access Denied
              </h1>
              <p className="text-sm sm:text-base text-slate-600">
                You need admin privileges to access this activity log.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Activity type options
  const activityTypes = [
    { value: "all", label: "All Activities" },
    { value: "USER_REGISTERED", label: "User Registration" },
    { value: "CAMPAIGN_CREATED", label: "Campaign Created" },
    { value: "CAMPAIGN_UPDATED", label: "Campaign Updated" },
    { value: "COMMENT_POSTED", label: "Comment Posted" },
    { value: "RESPONSE_SUBMITTED", label: "Response Submitted" },
    { value: "BLOOM_SCORE_GIVEN", label: "Bloom Score Given" },
    { value: "MILESTONE_REACHED", label: "Milestone Reached" },
    { value: "SIGNIFICANT_ENGAGEMENT", label: "Significant Engagement" },
  ];

  const handleLoadMore = () => {
    setFilters((prev) => ({
      ...prev,
      offset: (prev.offset || 0) + (prev.limit || 50),
    }));
  };

  const handleResetFilters = () => {
    setFilters({ limit: 50, offset: 0 });
    setSearchTerm("");
    setSelectedType("all");
  };

  const handleExportActivities = () => {
    // TODO: Implement export functionality
    toast({
      title: "Export functionality",
      description: "Export feature will be implemented soon.",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-primary/5">
      <NavBar />
      <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16 max-w-7xl">
        {/* Back Button */}
        <motion.div
          className="mb-4 sm:mb-6"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Button
            variant="ghost"
            onClick={() => navigate("/admin")}
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 text-sm sm:text-base"
          >
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Back</span>
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-r from-primary to-accent">
                <Activity className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Activity Log
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">
                  Comprehensive view of all platform activities and events
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportActivities}
                className="text-xs sm:text-sm"
              >
                <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Export</span>
                <span className="sm:hidden">Export</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetFilters}
                className="text-xs sm:text-sm"
              >
                <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Reset Filters</span>
                <span className="sm:hidden">Reset</span>
              </Button>
            </div>
          </div>

          {/* Stats Overview */}
          {!statsLoading && activityStats && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8"
            >
              <Card className="border-0 shadow-lg bg-gradient-to-br from-card/50 to-card/30 backdrop-blur-sm">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                      <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Total Activities
                      </p>
                      <p className="text-lg sm:text-2xl font-bold">
                        {activityStats.totalActivities || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-gradient-to-br from-card/50 to-card/30 backdrop-blur-sm">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg">
                      <Users className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        User Activities
                      </p>
                      <p className="text-lg sm:text-2xl font-bold">
                        {activityStats.userActivities || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-gradient-to-br from-card/50 to-card/30 backdrop-blur-sm">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 bg-purple-100 rounded-lg">
                      <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Campaign Activities
                      </p>
                      <p className="text-lg sm:text-2xl font-bold">
                        {activityStats.campaignActivities || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-gradient-to-br from-card/50 to-card/30 backdrop-blur-sm">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 bg-orange-100 rounded-lg">
                      <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Today's Activities
                      </p>
                      <p className="text-lg sm:text-2xl font-bold">
                        {activityStats.todayActivities || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-6 sm:mb-8"
          >
            <Card className="border-0 shadow-lg bg-card/50 backdrop-blur-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
                  Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs sm:text-sm font-medium">
                      Search
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search activities, users, or campaigns..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 text-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs sm:text-sm font-medium">
                      Activity Type
                    </label>
                    <Select
                      value={selectedType}
                      onValueChange={setSelectedType}
                    >
                      <SelectTrigger className="text-sm">
                        <SelectValue placeholder="Select activity type" />
                      </SelectTrigger>
                      <SelectContent>
                        {activityTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs sm:text-sm font-medium">
                      Results per page
                    </label>
                    <Select
                      value={filters.limit?.toString() || "50"}
                      onValueChange={(value) =>
                        setFilters((prev) => ({
                          ...prev,
                          limit: parseInt(value),
                          offset: 0,
                        }))
                      }
                    >
                      <SelectTrigger className="text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25 per page</SelectItem>
                        <SelectItem value="50">50 per page</SelectItem>
                        <SelectItem value="100">100 per page</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Activities List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card className="border-0 shadow-xl bg-card/50 backdrop-blur-sm">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-lg sm:text-xl">
                      Platform Activities
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm">
                      Showing {filteredActivities.length} activities
                      {searchTerm && ` matching "${searchTerm}"`}
                      {selectedType !== "all" &&
                        ` of type "${
                          activityTypes.find((t) => t.value === selectedType)
                            ?.label
                        }"`}
                    </CardDescription>
                  </div>
                  {activitiesLoading && (
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                      <span className="text-xs sm:text-sm text-muted-foreground">
                        Loading...
                      </span>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {activitiesError ? (
                  <div className="text-center py-6 sm:py-8">
                    <XCircle className="w-8 h-8 sm:w-12 sm:h-12 text-red-500 mx-auto mb-3 sm:mb-4" />
                    <p className="text-red-600 mb-2 text-sm sm:text-base">
                      Failed to load activities
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => window.location.reload()}
                      className="text-xs sm:text-sm"
                    >
                      <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      Retry
                    </Button>
                  </div>
                ) : filteredActivities.length === 0 ? (
                  <div className="text-center py-6 sm:py-8">
                    <Activity className="w-8 h-8 sm:w-12 sm:h-12 text-muted-foreground mx-auto mb-3 sm:mb-4" />
                    <p className="text-muted-foreground text-sm sm:text-base">
                      {searchTerm || selectedType !== "all"
                        ? "No activities match your current filters"
                        : "No activities found"}
                    </p>
                    {(searchTerm || selectedType !== "all") && (
                      <Button
                        variant="outline"
                        onClick={handleResetFilters}
                        className="mt-4 text-xs sm:text-sm"
                      >
                        Clear Filters
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredActivities.map((activity, index) => (
                      <motion.div
                        key={activity.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        <ActivityItem activity={activity} />
                      </motion.div>
                    ))}

                    {/* Load More Button */}
                    {activities &&
                      activities.length >= (filters.limit || 50) && (
                        <div className="text-center pt-4 sm:pt-6">
                          <Button
                            onClick={handleLoadMore}
                            disabled={activitiesLoading}
                            variant="outline"
                            className="text-xs sm:text-sm"
                          >
                            {activitiesLoading ? (
                              <>
                                <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
                                <span className="hidden sm:inline">
                                  Loading...
                                </span>
                                <span className="sm:hidden">Loading...</span>
                              </>
                            ) : (
                              <>
                                <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                <span className="hidden sm:inline">
                                  Load More Activities
                                </span>
                                <span className="sm:hidden">Load More</span>
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminActivity;
